import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const PwField = ({ id, label, value, onChange, show }) => (
  <div className="space-y-2">
    <Label htmlFor={id}>{label}</Label>
    <Input id={id} data-testid={`${id}-input`} type={show ? "text" : "password"} required className="h-11 rounded-sm" value={value} onChange={(e) => onChange(e.target.value)} />
  </div>
);

export default function ChangePassword() {
  const { user, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ current: "", next: "", confirm: "" });
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (f.next !== f.confirm) return setError("New passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: f.current, new_password: f.next });
      await refreshMe();
      toast.success("Password updated");
      navigate(user?.role === "admin" ? "/admin" : "/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center px-4 py-16">
      <form onSubmit={submit} data-testid="change-password-form" className="fade-up w-full max-w-sm space-y-5">
        <div>
          <div className="overline-label">First sign-in</div>
          <h1 className="mt-2 font-heading text-3xl font-black tracking-tight">Set your own password</h1>
          <p className="mt-2 text-sm text-muted-foreground">Your administrator gave you a temporary password. Choose a new one (8+ characters) to continue.</p>
        </div>
        <PwField id="current-password" label="Temporary password" value={f.current} onChange={(v) => setF({ ...f, current: v })} show={show} />
        <PwField id="new-password" label="New password" value={f.next} onChange={(v) => setF({ ...f, next: v })} show={show} />
        <PwField id="confirm-password" label="Confirm new password" value={f.confirm} onChange={(v) => setF({ ...f, confirm: v })} show={show} />
        <button type="button" data-testid="change-password-toggle" onClick={() => setShow(!show)} className="flex items-center gap-2 text-xs font-semibold text-muted-foreground hover:text-foreground">
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}{show ? "Hide passwords" : "Show passwords"}
        </button>
        {error && <p data-testid="change-password-error" role="alert" className="text-sm text-destructive">{error}</p>}
        <button type="submit" disabled={busy} data-testid="change-password-submit" className="h-11 w-full rounded-sm bg-brand-primary text-sm font-semibold text-brand-primary-fg hover:opacity-90 disabled:opacity-60">{busy ? "Saving…" : "Save and continue"}</button>
      </form>
    </div>
  );
}
